import {
  ArrowPathIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import React, { useEffect, useState } from "react";
import {
  AreaFormData,
  areasService,
  OrganizationArea,
} from "../services/areas";

interface AreasManagementProps {
  organizationId: string;
  orgRole: string;
  _organization: any;
}

const getInitialFormData = (): AreaFormData => ({
  name: "",
  description: "",
  value: "",
  color: "#3B82F6",
  icon: "",
  is_active: true,
  display_order: 0,
});

const COLOR_PRESETS = [
  "#3B82F6", // Blue
  "#10B981", // Green
  "#F59E0B", // Yellow
  "#EF4444", // Red
  "#8B5CF6", // Purple
  "#EC4899", // Pink
  "#6B7280", // Gray
  "#F97316", // Orange
  "#14B8A6", // Teal
];

export default function AreasManagement({
  organizationId,
  orgRole,
  _organization,
}: AreasManagementProps) {
  const [areas, setAreas] = useState<OrganizationArea[]>([]);
  const [open, setOpen] = useState(false);
  const [editingArea, setEditingArea] = useState<OrganizationArea | null>(null);
  const [formData, setFormData] = useState<AreaFormData>(getInitialFormData());
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);

  const canEdit = orgRole === "admin" || orgRole === "editor";
  const canDelete = orgRole === "admin";

  useEffect(() => {
    fetchAreas();
  }, [organizationId, showInactive]);

  const fetchAreas = async () => {
    try {
      const data = await areasService.list(organizationId, showInactive);
      setAreas(data);
    } catch (error) {
      // console.error removed
    }
  };

  const handleInitializeDefaults = async () => {
    if (
      !window.confirm(
        "This will create the default functional areas for your organization. Continue?",
      )
    ) {
      return;
    }

    setInitializing(true);
    try {
      await areasService.initializeDefaults(organizationId);
      fetchAreas();
    } catch (err: any) {
      alert(err.response?.data?.detail || "Failed to initialize default areas");
    } finally {
      setInitializing(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (editingArea) {
        // Update existing area
        await areasService.update(organizationId, editingArea.id, formData);
      } else {
        // Create new area
        await areasService.create(organizationId, formData);
      }
      setOpen(false);
      setEditingArea(null);
      setFormData(getInitialFormData());
      fetchAreas();
    } catch (err: any) {
      setError(err.response?.data?.detail || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (area: OrganizationArea) => {
    setEditingArea(area);
    setFormData({
      name: area.name,
      description: area.description || "",
      value: area.value,
      color: area.color || "#3B82F6",
      icon: area.icon || "",
      is_active: area.is_active,
      display_order: area.display_order,
    });
    setOpen(true);
  };

  const handleToggleActive = async (area: OrganizationArea) => {
    try {
      await areasService.update(organizationId, area.id, {
        is_active: !area.is_active,
      });
      fetchAreas();
    } catch (err: any) {
      alert(err.response?.data?.detail || "Failed to update area");
    }
  };

  const handleDelete = async (area: OrganizationArea) => {
    const confirmMessage = area.is_default
      ? `Are you sure you want to delete the default area "${area.name}"? Any tasks using this area will be reassigned to "Unassigned".`
      : `Are you sure you want to delete the area "${area.name}"? Any tasks using this area will be reassigned to "Unassigned".`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const response = await areasService.delete(organizationId, area.id);

      // Show success message with task reassignment info
      if (response && response.data && response.data.tasks_reassigned > 0) {
        alert(
          `Area deleted successfully. ${response.data.tasks_reassigned} task(s) were reassigned to "Unassigned".`,
        );
      }

      fetchAreas();
    } catch (err: any) {
      alert(err.response?.data?.detail || "Failed to delete area");
    }
  };

  const handleClose = () => {
    setOpen(false);
    setEditingArea(null);
    setFormData(getInitialFormData());
    setError(null);
  };

  // Generate value from name
  const generateValue = (name: string) => {
    return name
      .trim()
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .replace(/\s+/g, "_");
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Functional Areas
          </h2>
          {areas.length === 0 && canEdit && (
            <button
              onClick={handleInitializeDefaults}
              disabled={initializing}
              className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {initializing ? (
                <>
                  <ArrowPathIcon className="animate-spin -ml-0.5 mr-2 h-4 w-4" />
                  Initializing...
                </>
              ) : (
                "Initialize Default Areas"
              )}
            </button>
          )}
          <label className="flex items-center text-sm text-gray-600">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            Show inactive
          </label>
        </div>
        {canEdit && areas.length > 0 && (
          <button
            onClick={() => setOpen(true)}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
            Add Area
          </button>
        )}
      </div>

      {/* Areas List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {areas.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No functional areas found.</p>
            {canEdit && (
              <p className="mt-2 text-sm text-gray-400">
                Click &ldquo;Initialize Default Areas&rdquo; to get started with
                recommended areas.
              </p>
            )}
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {areas.map((area) => (
              <li key={area.id} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    {area.color && (
                      <div
                        className="w-4 h-4 rounded-full mr-3 flex-shrink-0"
                        style={{ backgroundColor: area.color }}
                      />
                    )}
                    <div>
                      <div className="flex items-center">
                        <h3 className="text-sm font-medium text-gray-900">
                          {area.name}
                        </h3>
                        <span className="ml-2 px-2 inline-flex text-xs leading-5 font-mono rounded-full bg-gray-100 text-gray-800">
                          {area.value}
                        </span>
                        {area.is_default && (
                          <span className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                            Default
                          </span>
                        )}
                        {!area.is_active && (
                          <span className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                            Inactive
                          </span>
                        )}
                      </div>
                      {area.description && (
                        <p className="text-sm text-gray-500 mt-1">
                          {area.description}
                        </p>
                      )}
                      {area.created_by_name && (
                        <p className="text-xs text-gray-400 mt-1">
                          Created by {area.created_by_name}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {canEdit && (
                      <button
                        onClick={() => handleToggleActive(area)}
                        className="text-gray-400 hover:text-gray-600"
                        title={area.is_active ? "Deactivate" : "Activate"}
                      >
                        {area.is_active ? "Deactivate" : "Activate"}
                      </button>
                    )}
                    {canEdit && (
                      <button
                        onClick={() => handleEdit(area)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                    )}
                    {canDelete && (
                      <button
                        onClick={() => handleDelete(area)}
                        className="text-gray-400 hover:text-red-600"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Create/Edit Modal */}
      {open && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500/75 transition-opacity"
              onClick={handleClose}
            />

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900">
                      {editingArea ? "Edit Area" : "Create New Area"}
                    </h3>
                    <button
                      type="button"
                      onClick={handleClose}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>

                  {error && (
                    <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
                      {error}
                    </div>
                  )}

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => {
                          const name = e.target.value;
                          setFormData({
                            ...formData,
                            name,
                            value: editingArea
                              ? formData.value
                              : generateValue(name),
                          });
                        }}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Value (Internal Code) *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.value}
                        onChange={(e) =>
                          setFormData({ ...formData, value: e.target.value })
                        }
                        disabled={!!editingArea}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm font-mono bg-gray-50"
                        placeholder="Auto-generated from name"
                      />
                      {!editingArea && (
                        <p className="mt-1 text-xs text-gray-500">
                          This value is used internally and cannot be changed
                          after creation
                        </p>
                      )}
                      {editingArea?.is_default && (
                        <p className="mt-1 text-xs text-yellow-600">
                          Default area values cannot be changed
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Description
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            description: e.target.value,
                          })
                        }
                        rows={3}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Color
                      </label>
                      <div className="mt-2 flex items-center space-x-2">
                        {COLOR_PRESETS.map((color) => (
                          <button
                            key={color}
                            type="button"
                            onClick={() => setFormData({ ...formData, color })}
                            className={`w-8 h-8 rounded-full border-2 ${
                              formData.color === color
                                ? "border-gray-900"
                                : "border-gray-300"
                            }`}
                            style={{ backgroundColor: color }}
                          />
                        ))}
                        <input
                          type="color"
                          value={formData.color || "#3B82F6"}
                          onChange={(e) =>
                            setFormData({ ...formData, color: e.target.value })
                          }
                          className="ml-2 h-8 w-16"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Display Order
                      </label>
                      <input
                        type="number"
                        value={formData.display_order}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            display_order: parseInt(e.target.value) || 0,
                          })
                        }
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="is_active"
                        checked={formData.is_active}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            is_active: e.target.checked,
                          })
                        }
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label
                        htmlFor="is_active"
                        className="ml-2 block text-sm text-gray-900"
                      >
                        Active
                      </label>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  >
                    {loading ? "Saving..." : editingArea ? "Update" : "Create"}
                  </button>
                  <button
                    type="button"
                    onClick={handleClose}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
